import React, { lazy, Suspense, useEffect } from 'react';
import LoginPage from './components/LoginPage';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GET_CURRENT_USER_REQUEST, getFetchRequest } from './store/requests';
import { CHANGE_VISIBILITY_EVENT, VISIBILITY_STATE_VISIBLE } from './constant';
import ProfilePage from './components/ProfilePage';
import UsersPage from './components/UsersPage';
import ProductPage from './components/ProductPage';
import RecipePage from './components/recipes/RecipePage';
import GenerationTracesPage from './components/generationTraces/GenerationTracesPage';
import ReferenceBooksPage from './components/referenceBooks/ReferenceBooksPage';
import GrainLiquidRatiosPage from './components/grainLiquidRatios/GrainLiquidRatiosPage';
import SvgLibraryPage from './components/svgLibrary/SvgLibraryPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import PreferencePage from './components/PreferencePage';
import { useTranslation } from 'react-i18next';
import WithWrapper from './components/WithMenu';
import { handleCreateNewWebSocketAsyncService, registerObserver, unRegisterObserver } from './shared/WebSocketConnectionService';
import { broadcastChannelService } from './shared/broadcastChannelService';
import { makeUrl, successCallbackEmpty } from './util';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { PageLoading } from './components/Loading';

/** Lazy: @chapche/draw must not load on login (own i18n must not touch admin). */
const SvgLibraryEditorPage = lazy(() => import('./components/svgLibrary/SvgLibraryEditorPage'));


const ProtectedLayout = () => (
    <RequireAuth>
        <WithWrapper />
    </RequireAuth>
);

const AppRoutes = () => {
    const { user, loading, setUser } = useAuth();
    const { i18n } = useTranslation();

    useEffect(() => {
        if (user?.locale) {
            i18n.changeLanguage(user.locale);
        }
    }, [user?.locale, i18n]);

    useEffect(() => {
        broadcastChannelService.createLocaleConnection();
        broadcastChannelService.createFormUpdateConnection();
        const onVisibility = () => {
            if (document.visibilityState === VISIBILITY_STATE_VISIBLE) {
                getFetchRequest(
                    makeUrl([GET_CURRENT_USER_REQUEST]),
                    successCallbackEmpty,
                    () => setUser(null),
                );
            }
        };
        document.addEventListener(CHANGE_VISIBILITY_EVENT, onVisibility);
        return () => document.removeEventListener(CHANGE_VISIBILITY_EVENT, onVisibility);
    }, [setUser]);

    useEffect(() => {
        if (!user) {
            return undefined;
        }
        handleCreateNewWebSocketAsyncService();
        const changeWSMessage = (message) => {
            console.log(message);
        };
        registerObserver(changeWSMessage);
        return () => unRegisterObserver(changeWSMessage);
    }, [user]);

    if (loading) {
        return <PageLoading />;
    }

    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Public: email-api links use create-password / password-reset / confirm-email */}
            <Route path="/password-reset" element={<ResetPasswordPage />} />
            <Route path="/password-reset/" element={<ResetPasswordPage />} />
            <Route path="/create-password" element={<ResetPasswordPage />} />
            <Route path="/create-password/" element={<ResetPasswordPage />} />
            <Route element={<ProtectedLayout />}>
                <Route path="/" element={<Navigate to="/profile" replace />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/user" element={<UsersPage />} />
                <Route path="/product/:tab/:id" element={<ProductPage />} />
                <Route path="/preference/:tab/:id" element={<PreferencePage />} />
                <Route path="/recipe/:tab/:id" element={<RecipePage />} />
                <Route path="/generation-trace" element={<GenerationTracesPage />} />
                <Route path="/generation-trace/:generationId" element={<GenerationTracesPage />} />
                <Route path="/reference-books" element={<ReferenceBooksPage />} />
                <Route path="/reference-books/:bookId" element={<ReferenceBooksPage />} />
                <Route path="/reference-books/:bookId/tables/:tableId" element={<ReferenceBooksPage />} />
                <Route path="/grain-liquid-ratios" element={<GrainLiquidRatiosPage />} />
                <Route path="/svg-library" element={<SvgLibraryPage />} />
                <Route
                    path="/svg-library/new"
                    element={(
                        <Suspense fallback={<PageLoading />}>
                            <SvgLibraryEditorPage mode="create" />
                        </Suspense>
                    )}
                />
                <Route
                    path="/svg-library/:assetId/edit"
                    element={(
                        <Suspense fallback={<PageLoading />}>
                            <SvgLibraryEditorPage mode="edit" />
                        </Suspense>
                    )}
                />
                <Route path="*" element={<Navigate to="/profile" replace />} />
            </Route>
        </Routes>
    );
};

const App = () => (
    <AuthProvider>
        <AppRoutes />
    </AuthProvider>
);

export default App;
